import type { RefObject } from 'react';

import type { CaseSummary } from '../../api/types';
import { dayWord, fmtDate } from '../../lib/format';
import { categoryLabel } from '../../lib/categories';
import { sourceLabel } from '../../lib/stages';

interface Chip {
    text: string;
    tone?: 'hot' | 'warn' | 'ok';
}

/**
 * Чипы шапки: порядок и формулировки — ui-case-card.md §4.3.
 * «Эскалация» и источник добавлены по ТЗ §8.5; чипа с суммой требования нет —
 * суммы в данных не существует (`_corrections.md` §3).
 */
function buildChips(item: CaseSummary): Chip[] {
    const chips: Chip[] = [];
    const left = item.days_left ?? 0;
    const closed = item.stage === 'sent' || Boolean(item.closed_at);

    if (closed) {
        chips.push({ text: 'Закрыто ' + (fmtDate(item.closed_at ?? item.sent_at) || 'дата не указана'), tone: 'ok' });
    } else if (left < 0) {
        const late = item.overdue_days ?? Math.abs(left);
        chips.push({ text: 'Просрочено на ' + late + ' ' + dayWord(late), tone: 'hot' });
    } else if (left === 0) {
        chips.push({ text: 'В работе · срок сегодня', tone: 'warn' });
    } else {
        chips.push({
            text: 'В работе · осталось ' + left + ' ' + dayWord(left),
            tone: left <= 3 ? 'warn' : undefined,
        });
    }

    // Стадию показывает дорожка стадий под шапкой — второй раз в чипах её нет.

    if (item.priority === 1) chips.push({ text: 'Приоритет 1', tone: 'hot' });
    if (item.escalation_risk) chips.push({ text: '🛡 Риск проверки', tone: 'hot' });
    if (item.escalated) chips.push({ text: '🛡 Эскалация', tone: 'hot' });
    if (item.extended) chips.push({ text: '⏳ Продлено', tone: 'warn' });
    if (item.repeat_of) chips.push({ text: '🔁 Повторное' });
    if (item.primary_category) chips.push({ text: categoryLabel(item.primary_category) });
    if (item.department_name) chips.push({ text: item.department_name });
    chips.push({
        text: item.executor_name ?? 'Без исполнителя',
        tone: item.executor_name ? undefined : 'warn',
    });
    chips.push({ text: sourceLabel(item.source) });
    // Источник «Демо-данные» уже говорит, что дело демонстрационное: второй чип «демо» — только для других источников.
    if (item.demo && item.source !== 'demo') chips.push({ text: 'демо' });

    return chips;
}

export interface CaseHeaderProps {
    titleId: string;
    caseItem: CaseSummary | null;
    title: string;
    loading: boolean;
    error: string | null;
    backVisible: boolean;
    backText: string;
    onBack: () => void;
    onClose: () => void;
    closeRef: RefObject<HTMLButtonElement | null>;
}

/** Тёмная шапка модалки: номер, приоритет, тема, чипы состояния. */
export function CaseHeader({
    titleId,
    caseItem,
    title,
    loading,
    error,
    backVisible,
    backText,
    onBack,
    onClose,
    closeRef,
}: CaseHeaderProps) {
    const chips = caseItem ? buildChips(caseItem) : [];
    const idLine = caseItem ? (
        <>
            {caseItem.number}
            <span className="m-prio">{' · приоритет ' + caseItem.priority}</span>
        </>
    ) : loading ? (
        'Загрузка…'
    ) : (
        'Дело недоступно'
    );

    return (
        <div className="modal-head">
            <div className="mh-text">
                <button type="button" className="back-btn" onClick={onBack} hidden={!backVisible}>
                    {backText}
                </button>
                <div className="m-id">{idLine}</div>
                <h3 id={titleId} title={error ?? title}>
                    {error ?? title}
                </h3>
                <div className="m-chips">
                    {chips.map((chip, index) => (
                        <span key={chip.text + '#' + index} className={chip.tone}>
                            {chip.text}
                        </span>
                    ))}
                </div>
            </div>
            <button type="button" ref={closeRef} onClick={onClose} aria-label="Закрыть">
                ✕
            </button>
        </div>
    );
}

export default CaseHeader;
